import { useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { navigationHistory } from "../utils/NavigationHistory";

export const useNavigationHistory = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const scrollPositions = useRef({});
  const pageStates = useRef({});

  // Save current scroll position before navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      scrollPositions.current[location.pathname] = window.scrollY || 0;
    };

    window.addEventListener("scroll", () => {
      scrollPositions.current[location.pathname] = window.scrollY || 0;
    });

    return () => {
      window.removeEventListener("scroll", handleBeforeUnload);
    };
  }, [location.pathname]);

  // Track page visits
  useEffect(() => {
    const scrollPos = scrollPositions.current[location.pathname] || 0;
    const pageState = pageStates.current[location.pathname] || {};

    navigationHistory.addPage(location.pathname, scrollPos, pageState);

    // Restore scroll position on page load
    setTimeout(() => {
      const savedScrollPos = scrollPositions.current[location.pathname];
      if (savedScrollPos !== undefined) {
        window.scrollTo(0, savedScrollPos);
      } else {
        window.scrollTo(0, 0);
      }
    }, 0);
  }, [location.pathname]);

  // Handle browser back button
  const handleGoBack = useCallback(() => {
    const previousPage = navigationHistory.getPreviousPage();

    if (previousPage) {
      navigate(previousPage.path);

      // Restore scroll position
      setTimeout(() => {
        window.scrollTo(0, previousPage.scrollPosition || 0);
      }, 0);
    } else {
      navigate(-1); // Fallback to browser default
    }
  }, [navigate]);

  // Save page state (useful for preserving filter state, etc.)
  const savePageState = useCallback(
    (state) => {
      pageStates.current[location.pathname] = state;
    },
    [location.pathname],
  );

  // Get saved page state
  const getPageState = useCallback(() => {
    return pageStates.current[location.pathname] || {};
  }, [location.pathname]);

  return {
    handleGoBack,
    savePageState,
    getPageState,
    navigationHistory,
  };
};
